/**
 * Utlegg — a staff member's expense claims, with receipts, approved by someone else, marked paid.  MKK module 23
 * «Utlegg» (B2, 2026-10-24). Staff only; nothing here is public or in the member app.
 *
 * Behind the `expenses` capability (the menu item is absent without it, and every route answers 404).
 *   expenses.submit    «Mine utlegg»: send a claim, change it while it waits, add receipts, withdraw it;
 *   expenses.approve   «Til godkjenning»: approve (a receipt needed) or reject with a reason — never one's own;
 *   expenses.pay       «Betalt» and the approved ones: mark paid with the bank's reference; the CSV for the accountant.
 * The server says what this person may do with each claim (`can`) and decides again on each request.
 *
 * Approve, reject and pay take two clicks (TwoStep). Receipts are opened with the session (an audited download), never
 * as a bare link. The account number is shown in full only to its owner and to a payer.
 *
 * «Bekreftet med Vipps» (owner 2026-10-29; the server's vipps-signature.js): the claimant presses «Bekreft med Vipps»; an
 * approver may press «Godkjenn og bekreft med Vipps», which approves the claim when Vipps comes back. The page goes to
 * Vipps and comes back to #/utlegg?sig=<one-time code>; this view spends the code with the person's own session
 * (POST /signatures/finish) — the code alone confirms nothing. Every text says «bekreftet», never «signatur»: a Vipps
 * Login is identification bound to the claim's content, not a signature. The block is green while it counts, grey once
 * the claim changed; a Vipps name that differs from the account's name is a yellow warning for the approver.
 *
 * Money-pro (owner 2026-10-31): «Per måned» — a month selector, «Utlegg per måned» over twelve months (approved and paid
 * green, waiting grey) and the month's totals per category; «Opplastingslenker» (approver or payer) — a link for someone
 * outside the panel to upload a receipt, its address shown ONCE; a claim that came through one is tagged «Via lenke» and is
 * approved like any other, with no claimant confirmation («Uten Vipps-bekreftelse fra innsender»). When the platform has an
 * AI key, the new-claim form has «Les kvitteringen»: the chosen receipt is read once and the fields are PREFILLED — the
 * person checks them and sends; nothing is saved from the reading alone.
 */
import React, { useRef, useState } from 'react';
import { BarChart, MONTHS_NO } from '../components/BarChart';
import { can, download, errSummary, errText, fieldErrors, patch, post, type Body } from '../lib/api';
import { useLoad, useMsg, dropChanged } from '../lib/hooks';
import { kb, longDate, osloTime, isoToday } from '../lib/format';
import { Body as CardBody, Btn, Card, CopyField, Field, FieldError, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { Stamp, VoidedStamps, startConfirm, useVippsReturn } from '../components/VippsConfirm';
import { Expenses } from '../core';

const CAT = Expenses.CATEGORY_NO;
const STATUS_TONE: Record<string, TagTone> = { submitted: 'gold', approved: 'green', paid: 'green', rejected: 'red', withdrawn: 'grey' };
export const statusTag = (s: string) => <Tag tone={STATUS_TONE[s] || 'grey'}>{Expenses.STATUS_NO[s] || s}</Tag>;
const krText = (ore: number) => Expenses.kr(ore);

/** Under a confirmation that stopped counting. */
const CHANGED = 'Bekreftelsen gjelder ikke lenger — utlegget er endret.';

const EMPTY = { title: '', amount: '', spentOn: '', category: 'food', description: '', accountNo: '' };
type Form = typeof EMPTY;
const toForm = (c: Body): Form => ({ title: c.title || '', amount: Expenses.formatKr(c.amountOre, { grouped: false }), spentOn: c.spentOn || '',
  category: c.category || 'other', description: c.description || '', accountNo: c.accountNo ? Expenses.accountText(c.accountNo) : '' });

/** A file as base64 for the receipt route (PDF, JPEG, PNG, HEIC; the server decides by the bytes). */
function readReceipt(file: File): Promise<{ filename: string; data: string }> {
  return new Promise((resolve, reject) => {
    if (file.size > Expenses.MAX_RECEIPT_BYTES) return reject(new Error('Kvitteringen er større enn 8 MB.'));
    const fr = new FileReader();
    fr.onerror = () => reject(new Error('Filen kunne ikke leses.'));
    fr.onload = () => {
      const b = new Uint8Array(fr.result as ArrayBuffer); let bin = '';
      for (let i = 0; i < b.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(b.subarray(i, i + 0x8000)));
      resolve({ filename: file.name, data: btoa(bin) });
    };
    fr.readAsArrayBuffer(file);
  });
}

function ClaimForm({ f, set, errors, today }: { f: Form; set: (f: Form) => void; errors: Record<string, string>; today: string }) {
  // FieldError: red under the field and a toast (2026-10-31).
  const err = (k: string) => <FieldError text={errors[k]} />;
  return (
    <>
      <div className="row">
        <Field label="Hva gjelder utlegget" required className="field">{(id) => <><input id={id} maxLength={Expenses.MAX_TITLE} autoComplete="off" value={f.title}
          onChange={(e) => set({ ...f, title: e.target.value })} />{err('title')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Beløp (kr)" required className="field half" note="For eksempel 149 eller 149,50.">{(id, d) => <><input id={id} aria-describedby={d} inputMode="decimal"
          autoComplete="off" value={f.amount} onChange={(e) => set({ ...f, amount: e.target.value })} />{err('amountOre')}</>}</Field>
        <Field label="Dato" required className="field half">{(id) => <><input id={id} type="date" max={today} value={f.spentOn}
          onChange={(e) => set({ ...f, spentOn: e.target.value })} />{err('spentOn')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Kategori" required className="field half">{(id) => <><select id={id} value={f.category} onChange={(e) => set({ ...f, category: e.target.value })}>
          {Expenses.CATEGORIES.map((c) => <option key={c} value={c}>{CAT[c]}</option>)}</select>{err('category')}</>}</Field>
        <Field label="Kontonummer" required className="field half" note="Elleve sifre. Pengene overføres hit.">{(id, d) => <><input id={id} aria-describedby={d} inputMode="numeric"
          autoComplete="off" value={f.accountNo} onChange={(e) => set({ ...f, accountNo: e.target.value })} />{err('accountNo')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Beskrivelse" className="field">{(id) => <><textarea id={id} rows={3} maxLength={Expenses.MAX_DESCRIPTION} value={f.description}
          onChange={(e) => set({ ...f, description: e.target.value })} />{err('description')}</>}</Field>
      </div>
    </>
  );
}

/** One claim: the facts, the receipts (download; add while waiting), the trail, and the moves this person may make. */
function Detail({ id, onChanged, onClose }: { id: string; onChanged: () => void; onClose: () => void }) {
  const { data, error, reload } = useLoad<Body>(`/expenses/${id}`, [id]);
  const [msg, say] = useMsg();
  const [note, setNote] = useState('');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [going, setGoing] = useState(false);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const c: Body = data.claim;
  // A claim changed since it was opened is fetched again, so the next click decides what is now on screen.
  const done = (r: { ok: boolean; body: Body }, okText: string) => { say(r.ok ? okText : r.body.message || errText(r.body), r.ok ? 'ok' : 'err'); if (r.ok || r.body.code === 'claim_changed') { reload(); onChanged(); } };
  const move = async (what: 'approve' | 'reject' | 'pay' | 'withdraw') => {
    // The decision is about the claim on screen: its updatedAt goes along, and the server refuses (409) one changed since.
    const seen = { expectedUpdatedAt: c.updatedAt };
    const body = what === 'pay' ? { ref: ref.trim() || undefined, ...seen } : what === 'withdraw' ? {} : { note: note.trim() || undefined, ...seen };
    done(await post(`/expenses/${c.id}/${what}`, body), { approve: 'Utlegget er godkjent.', reject: 'Utlegget er avvist.', pay: 'Utlegget er merket betalt.', withdraw: 'Utlegget er trukket.' }[what]);
  };
  const confirmVipps = async (role: 'claimant' | 'approver') => {
    if (going) return;
    setGoing(true);
    const e = await startConfirm('expense_claim', c.id, role);
    if (e) { say(e, 'err'); setGoing(false); }
  };
  const conf = c.confirmations || {};
  const addReceipt = async (file: File | undefined) => {
    if (!file || busy) return;
    setBusy(true);
    try { done(await post(`/expenses/${c.id}/receipts`, await readReceipt(file)), 'Kvitteringen er lagt ved.'); }
    catch (x) { say((x as Error).message, 'err'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  return (
    <CardBody>
      <div className="work-bar"><b>{c.title}</b> {statusTag(c.status)}<span className="sp" /><Btn ghost onClick={onClose}>Lukk</Btn></div>
      <table className="t">
        <tbody>
          <tr><th scope="row">Beløp</th><td>{krText(c.amountOre)}</td></tr>
          <tr><th scope="row">Dato</th><td>{longDate(c.spentOn)}</td></tr>
          <tr><th scope="row">Kategori</th><td>{CAT[c.category] || c.category}</td></tr>
          <tr><th scope="row">Sendt av</th><td>{c.claimantName || '—'}{c.viaLink && <> <Tag tone="gold">Via lenke</Tag> <span className="note">lenken laget av {c.linkMadeByYou ? 'deg — en annen må godkjenne' : c.linkMadeByName || '—'}</span></>}</td></tr>
          <tr><th scope="row">Kontonummer</th><td>{c.accountNo ? Expenses.accountText(c.accountNo) : c.accountMasked}</td></tr>
          {c.description && <tr><th scope="row">Beskrivelse</th><td style={{ whiteSpace: 'pre-line' }}>{c.description}</td></tr>}
          {c.decidedByName && <tr><th scope="row">{c.status === 'rejected' ? 'Avvist av' : 'Godkjent av'}</th><td>{c.decidedByName} · {osloTime(c.decidedAt)}{c.decisionNote ? ` — ${c.decisionNote}` : ''}</td></tr>}
          {c.paidByName && <tr><th scope="row">Betalt av</th><td>{c.paidByName} · {osloTime(c.paidAt)}{c.paidRef ? ` — ref. ${c.paidRef}` : ''}</td></tr>}
          {c.viaLink ? <tr><th scope="row">Bekreftet av utlegger</th><td>Uten Vipps-bekreftelse fra innsender</td></tr>
            : <Stamp label="Bekreftet av utlegger" s={conf.claimant || null} approver={!c.mine} changedText={CHANGED} />}
          <Stamp label="Godkjenning bekreftet" s={conf.approver || null} approver={!c.mine} changedText={CHANGED} />
        </tbody>
      </table>
      {c.confirmationRequired && c.status === 'submitted' && c.viaLink && <p className="note">Moskeen krever bekreftelse med Vipps: utlegget kom inn via en opplastingslenke, så det godkjennes med «Godkjenn og bekreft med Vipps» uten bekreftelse fra innsender.</p>}
      {c.confirmationRequired && c.status === 'submitted' && !c.viaLink && <p className="note">Moskeen krever bekreftelse med Vipps: utlegget godkjennes med «Godkjenn og bekreft med Vipps» etter at den som sendte det, har bekreftet det.</p>}
      {c.can.confirm && (
        <div className="row"><Btn small={false} disabled={going} onClick={() => confirmVipps('claimant')}>{going ? 'Åpner Vipps …' : conf.claimant && conf.claimant.valid ? 'Bekreft med Vipps på nytt' : 'Bekreft med Vipps'}</Btn>
          <span className="note"> Bekreft at det er du som ber om pengene. Endrer du utlegget etterpå, må du bekrefte igjen.</span></div>
      )}
      {c.can.approveWithVipps && (
        <div className="row"><Btn small={false} disabled={going} onClick={() => confirmVipps('approver')}>{going ? 'Åpner Vipps …' : 'Godkjenn og bekreft med Vipps'}</Btn></div>
      )}
      <VoidedStamps voided={conf.voided} changedWord="utlegget endret" />
      <div className="row"><Btn ghost onClick={async () => { const d = await download(`/v1/admin/expenses/${c.id}/pdf`, `utlegg-${c.spentOn}.pdf`); if (!d.ok) say(d.message || 'Kunne ikke hente PDF-en.', 'err'); }}>Last ned PDF</Btn></div>
      <h4>Kvitteringer</h4>
      {(c.receipts || []).length ? (
        <ul>{c.receipts.map((f: Body) => (
          <li key={f.id}><button type="button" className="linkish" onClick={async () => { const d = await download(`/v1/admin/expenses/${c.id}/receipts/${f.id}`, f.filename || 'kvittering'); if (!d.ok) say(d.message || 'Kunne ikke åpne kvitteringen.', 'err'); }}>
            {f.filename}</button> <span className="note">{kb(f.size)}</span></li>))}</ul>
      ) : <p className="note">Ingen kvitteringer ennå. Utlegget kan ikke godkjennes uten.</p>}
      {c.can.edit && (
        <div className="row">
          <label className="btn sm ghost">{busy ? 'Laster opp …' : '+ Legg ved kvittering'}
            <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/heic,.heic" hidden onChange={(e) => addReceipt(e.target.files?.[0])} />
          </label>
        </div>
      )}
      {(c.can.approve || c.can.reject) && (
        <div className="row">
          <Field label="Merknad" className="field" note="Må fylles ut ved avvisning.">{(fid, d) => <textarea id={fid} aria-describedby={d} rows={2} maxLength={Expenses.MAX_NOTE} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
          <TwoStep label="Godkjenn" ghost={false} confirmLabel="Klikk igjen for å godkjenne" busyLabel="Godkjenner …" onConfirm={() => move('approve')} />{' '}
          <TwoStep label="Avvis" confirmLabel="Klikk igjen for å avvise" busyLabel="Avviser …" onConfirm={() => move('reject')} />
        </div>
      )}
      {c.can.pay && (
        <div className="row">
          <Field label="Referanse fra banken" className="field half">{(fid) => <input id={fid} maxLength={Expenses.MAX_REF} value={ref} onChange={(e) => setRef(e.target.value)} />}</Field>
          <TwoStep label="Merk som betalt" ghost={false} confirmLabel="Klikk igjen: pengene er overført" busyLabel="Lagrer …" onConfirm={() => move('pay')} />
        </div>
      )}
      {c.can.withdraw && <div className="row"><TwoStep label="Trekk utlegget" confirmLabel="Klikk igjen for å trekke det" onConfirm={() => move('withdraw')} /></div>}
      <Msg text={msg.text} tone={msg.tone} />
      {(c.events || []).length > 0 && <>
        <h4>Historikk</h4>
        <ul className="note">{c.events.map((e: Body, i: number) => (
          <li key={i}>{osloTime(e.at)} · {e.actorName || '—'}: {e.from ? `${Expenses.STATUS_NO[e.from]} → ` : ''}{Expenses.STATUS_NO[e.to]}{e.note ? ` — ${e.note}` : ''}</li>))}</ul>
      </>}
    </CardBody>
  );
}

/** «Per måned»: a month selector, «Utlegg per måned» over twelve months, and the chosen month per category (money-pro G2). */
function MonthView({ today }: { today: string }) {
  const [month, setMonth] = useState(today.slice(0, 7));
  const { data, error, reload } = useLoad<Body>(`/expenses/summary?months=12&month=${month}`, [month]);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const months: Body[] = data.months || [];
  const label = (m: string) => `${MONTHS_NO[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;
  return (
    <CardBody>
      <div className="row">
        <Field label="Måned" className="field half">{(id) => <select id={id} value={month} onChange={(e) => setMonth(e.target.value)}>
          {[...months].reverse().map((m) => <option key={m.month} value={m.month}>{label(m.month)}</option>)}</select>}</Field>
      </div>
      <h4>Utlegg per måned</h4>
      <BarChart ariaLabel="Utlegg per måned: godkjent og betalt, og det som venter" unit="kr" seriesLabels={['Godkjent og betalt', 'Venter']}
        series={months.map((m) => ({ label: MONTHS_NO[Number(m.month.slice(5, 7)) - 1], value: m.approvedOre / 100 }))}
        secondary={months.map((m) => ({ label: m.month, value: m.submittedOre / 100 }))} />
      <p className="note">{data.scope === 'all' ? 'Alle utlegg i moskeen' : 'Dine egne utlegg'}, etter datoen pengene ble lagt ut. Grønt: godkjent og betalt. Grått: venter på godkjenning.</p>
      <h4>{label(month)} per kategori</h4>
      <table className="t">
        <thead><tr><th scope="col">Kategori</th><th scope="col">Godkjent og betalt</th><th scope="col">Venter</th><th scope="col">Antall</th></tr></thead>
        <tbody>{(data.month.categories || []).map((c: Body) => (
          <tr key={c.category}><td>{c.label_no}</td><td>{krText(c.approvedOre)}</td><td>{krText(c.submittedOre)}</td><td>{c.count}</td></tr>))}</tbody>
        <tfoot><tr><th scope="row">Sum</th><td>{krText(data.month.approvedOre)}</td><td>{krText(data.month.submittedOre)}</td><td /></tr></tfoot>
      </table>
    </CardBody>
  );
}

/**
 * «Opplastingslenker» (money-pro G3): a link someone outside the panel opens to send a claim with its receipt. The address
 * is shown ONCE (only its hash is kept); an open link can be revoked (two clicks).
 */
function Links() {
  const { data, error, reload } = useLoad<Body>('/expenses/links', []);
  const [msg, say] = useMsg();
  const [f, setF] = useState({ label: '', days: String(Expenses.DEFAULT_LINK_DAYS), maxUses: '1' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [made, setMade] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await post('/expenses/links', { label: f.label, days: Number(f.days), maxUses: Number(f.maxUses) });
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      setErrors({}); setMade(r.body.url); setF({ ...f, label: '' }); say(r.body.message, 'ok'); reload();
    } finally { setBusy(false); }
  };
  const revoke = async (id: string) => {
    const r = await post(`/expenses/links/${id}/revoke`, {});
    say(r.ok ? r.body.message : errSummary(r.body), r.ok ? 'ok' : 'err');
    reload();
  };
  const STATE_TONE: Record<string, TagTone> = { open: 'green', used: 'grey', expired: 'grey', revoked: 'red' };
  return (
    <CardBody>
      <p className="note">En lenke til en som ikke har tilgang til panelet: personen fyller ut et kort skjema og legger ved kvitteringen. Utlegget kommer inn her, merket «Via lenke», og godkjennes av en annen enn deg.</p>
      <div className="row">
        <Field label="Hvem lenken er til" required className="field half">{(id) => <><input id={id} maxLength={Expenses.MAX_LINK_LABEL} autoComplete="off" value={f.label}
          onChange={(e) => setF({ ...f, label: e.target.value })} /><FieldError text={errors.label} /></>}</Field>
        <Field label="Virker i (dager)" className="field">{(id) => <><input id={id} type="number" min={1} max={Expenses.MAX_LINK_DAYS} value={f.days}
          onChange={(e) => setF({ ...f, days: e.target.value })} /><FieldError text={errors.days} /></>}</Field>
        <Field label="Antall utlegg" className="field">{(id) => <><input id={id} type="number" min={1} max={Expenses.MAX_LINK_USES} value={f.maxUses}
          onChange={(e) => setF({ ...f, maxUses: e.target.value })} /><FieldError text={errors.maxUses} /></>}</Field>
        <Btn small={false} disabled={busy} onClick={make}>{busy ? 'Lager …' : 'Lag opplastingslenke'}</Btn>
      </div>
      {made && <CopyField label="Lenken (vises bare nå)" value={made} />}
      <Msg text={msg.text} tone={msg.tone} />
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <table className="t">
          <thead><tr><th scope="col">Til</th><th scope="col">Status</th><th scope="col">Brukt</th><th scope="col">Utløper</th><th scope="col">Laget av</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>{(data.links || []).length ? data.links.map((l: Body) => (
            <tr key={l.id}><td>{l.label}</td><td><Tag tone={STATE_TONE[l.state] || 'grey'}>{Expenses.LINK_STATE_NO[l.state] || l.state}</Tag></td>
              <td>{l.usedCount} av {l.maxUses}</td><td>{osloTime(l.expiresAt)}</td><td>{l.createdByName || '—'}</td>
              <td>{l.state === 'open' && <TwoStep label="Trekk tilbake" ariaLabel={`Trekk tilbake lenken til ${l.label}`} confirmLabel="Klikk igjen for å trekke tilbake" onConfirm={() => revoke(l.id)} />}</td></tr>
          )) : <tr><td colSpan={6}>Ingen lenker ennå.</td></tr>}</tbody>
        </table>
      )}
    </CardBody>
  );
}

type Tab = 'mine' | 'decide' | 'paid' | 'month' | 'links';
export function Utlegg() {
  const approver = can('expenses.approve'), payer = can('expenses.pay');
  const [tab, setTab] = useState<Tab>(can('expenses.submit') ? 'mine' : 'decide');
  const path = tab === 'mine' || (tab === 'month' && can('expenses.submit')) ? '/expenses/mine' : tab === 'paid' ? '/expenses?status=paid' : '/expenses?status=open';
  const { data, error, reload } = useLoad<Body>(path, [path]);
  const [open, setOpen] = useState<string | null>(null);
  const [msg, say] = useMsg();
  const [form, setForm] = useState<{ id: string | null; f: Form; start: Form } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // Money-pro G4: the receipt chosen in the new-claim form (stored with the claim after it is sent), and whether it was read.
  const [file, setFile] = useState<File | null>(null);
  const [readOf, setReadOf] = useState<File | null>(null);
  const [reading, setReading] = useState(false);
  const today = (data && data.today) || isoToday();
  const receiptRead = Boolean(data && data.receiptRead);
  /** «Les kvitteringen»: one AI reading per chosen file; the fields are PREFILLED, nothing is saved. */
  const readIt = async () => {
    if (!form || !file || reading || readOf === file) return;
    setReading(true);
    try {
      const r = await post('/expenses/receipt-read', await readReceipt(file));
      setReadOf(file);
      if (!r.ok) return say(r.body.message || errText(r.body), 'warn');
      const v = r.body.fields || {};
      // Functional: anything typed while the reading ran is kept.
      setForm((cur) => (cur ? { ...cur, f: { ...cur.f, ...(v.title && !cur.f.title ? { title: v.title } : {}), ...(v.amountOre ? { amount: Expenses.formatKr(v.amountOre, { grouped: false }) } : {}),
        ...(v.spentOn ? { spentOn: v.spentOn } : {}), ...(v.category ? { category: v.category } : {}) } } : cur));
      say(r.body.message, 'ok');
    } catch (x) { say((x as Error).message, 'err'); }
    finally { setReading(false); }
  };

  // Back from Vipps: the code leaves the address, is spent with this session, and the claim opens (components/VippsConfirm).
  useVippsReturn('/utlegg', say, (b) => {
    say(b.role === 'approver' ? 'Utlegget er godkjent og bekreftet med Vipps.' : 'Utlegget er bekreftet med Vipps.', 'ok');
    if (b.role === 'approver') setTab('decide');
    setOpen(b.subjectId); reload();
  });

  const save = async () => {
    if (!form || saving) return;
    const amount = Expenses.kroner(form.f.amount);
    if (!amount.ok) { setErrors({ amountOre: amount.message }); return; }
    setSaving(true);
    try {
      const body = { title: form.f.title, amountOre: amount.ore, spentOn: form.f.spentOn, category: form.f.category,
        description: form.f.description.trim() || null, accountNo: form.f.accountNo };
      const r = form.id ? await patch(`/expenses/${form.id}`, body) : await post('/expenses', body);
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      // The receipt chosen in the form goes with the new claim (the same route as «Legg ved kvittering»).
      let attached = false;
      if (!form.id && file && r.body.claim) {
        try { attached = (await post(`/expenses/${r.body.claim.id}/receipts`, await readReceipt(file))).ok; } catch { attached = false; }
      }
      setForm(null); setFile(null); setReadOf(null);
      say(form.id ? 'Utlegget er lagret.' : attached ? 'Utlegget er sendt med kvitteringen.' : 'Utlegget er sendt. Legg ved kvitteringen.', 'ok'); reload();
      if (!form.id && r.body.claim) setOpen(r.body.claim.id);
    } finally { setSaving(false); }
  };
  const exportCsv = async () => {
    const d = await download('/v1/admin/expenses/export.csv', `utlegg-${today}.csv`);
    if (!d.ok) say(d.message || 'Kunne ikke hente CSV-filen.', 'err');
  };

  const items: Body[] = (data && data.items) || [];
  return (
    <Card title="Utlegg" hint="Få pengene tilbake for det du har lagt ut for moskeen. Legg ved kvitteringen; en annen enn deg godkjenner, og den som betaler merker utlegget betalt.">
      <div className="work-bar">
        <div className="bnr-seg work-seg" role="group" aria-label="Utlegg">
          {can('expenses.submit') && <button type="button" aria-pressed={tab === 'mine'} onClick={() => { setTab('mine'); setOpen(null); }}>Mine utlegg</button>}
          {(approver || payer) && <button type="button" aria-pressed={tab === 'decide'} onClick={() => { setTab('decide'); setOpen(null); }}>Til godkjenning</button>}
          {(approver || payer) && <button type="button" aria-pressed={tab === 'paid'} onClick={() => { setTab('paid'); setOpen(null); }}>Betalt</button>}
          <button type="button" aria-pressed={tab === 'month'} onClick={() => { setTab('month'); setOpen(null); }}>Per måned</button>
          {(approver || payer) && <button type="button" aria-pressed={tab === 'links'} onClick={() => { setTab('links'); setOpen(null); }}>Opplastingslenker</button>}
        </div>
        <span className="sp" />
        {payer && tab === 'paid' && <Btn ghost onClick={exportCsv}>Last ned CSV (i år)</Btn>}
        {tab === 'mine' && <Btn small={false} onClick={() => { setErrors({}); say(''); setFile(null); setReadOf(null); setForm({ id: null, f: EMPTY, start: EMPTY }); }}>+ Nytt utlegg</Btn>}
      </div>
      {!form && <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>}
      {tab === 'month' ? <MonthView today={today} /> : tab === 'links' ? <Links /> : error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <>
          <p className="vh" role="status">{items.length === 1 ? '1 utlegg' : `${items.length} utlegg`}</p>
          <table className="t">
            <thead><tr><th scope="col">Hva</th>{tab !== 'mine' && <th scope="col">Sendt av</th>}<th scope="col">Dato</th><th scope="col">Beløp</th>
              <th scope="col">Status</th><th scope="col">Kvitteringer</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
            <tbody>{items.length ? items.map((c) => (
              <tr key={c.id}>
                <td><b>{c.title}</b><div className="sub">{CAT[c.category] || c.category}</div></td>
                {tab !== 'mine' && <td>{c.claimantName || '—'}{c.viaLink && <> <Tag tone="gold">Via lenke</Tag></>}</td>}
                <td>{c.spentOn}</td>
                <td>{krText(c.amountOre)}</td>
                <td>{statusTag(c.status)}{c.confirmations && c.confirmations.claimant && c.confirmations.claimant.valid && <> <Tag tone="green">Bekreftet med Vipps</Tag></>}</td>
                <td>{c.receiptCount}</td>
                <td><Btn ghost aria-label={`Åpne ${c.title}`} onClick={() => setOpen(open === c.id ? null : c.id)}>{open === c.id ? 'Skjul' : 'Åpne'}</Btn>
                  {c.can && c.can.edit && <> <Btn ghost aria-label={`Endre ${c.title}`} onClick={() => { setErrors({}); say(''); setForm({ id: c.id, f: toForm(c), start: toForm(c) }); }}>Endre</Btn></>}</td>
              </tr>)) : <tr><td colSpan={7}>{tab === 'mine' ? 'Du har ingen utlegg. Send det første med «Nytt utlegg».' : tab === 'paid' ? 'Ingen betalte utlegg.' : 'Ingen utlegg venter.'}</td></tr>}</tbody>
          </table>
          {open && <Detail id={open} onChanged={reload} onClose={() => setOpen(null)} />}
        </>
      )}
      <FormDialog open={Boolean(form)} title={form && form.id ? 'Endre utlegg' : 'Nytt utlegg'} dirty={Boolean(form) && JSON.stringify(form!.f) !== JSON.stringify(form!.start)}
        onClose={() => setForm(null)}>
        {form && <>
          {!form.id && (
            <div className="row">
              <label className="btn sm ghost">{file ? `Kvittering: ${file.name}` : '+ Velg kvittering'}
                <input type="file" accept="application/pdf,image/jpeg,image/png,image/heic,.heic" hidden onChange={(e) => { setFile(e.target.files?.[0] || null); setReadOf(null); }} />
              </label>
              {receiptRead && file && <Btn ghost disabled={reading || readOf === file} onClick={readIt}>{reading ? 'Leser …' : 'Les kvitteringen'}</Btn>}
              {receiptRead && <span className="note">Kvitteringer kan leses automatisk av en KI-tjeneste for å fylle ut beløp og dato; vi lagrer ikke noe fra det før du bekrefter.</span>}
            </div>
          )}
          <ClaimForm f={form.f} set={(f) => { setErrors(dropChanged(errors, form.f, f)); setForm({ ...form, f }); }} errors={errors} today={today} />
          <div className="row"><Btn small={false} disabled={saving} onClick={save}>{saving ? 'Lagrer …' : form.id ? 'Lagre' : 'Send'}</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
    </Card>
  );
}
